import axios from 'axios';
import { authService } from '~/services/auth-service';
import type { AuthUser, PbAccessToken } from '~/types/auth/auth';

export const useAuthStore = defineStore(
    'auth',
    () => {
        const config = useRuntimeConfig();
        const encryption = useEncryption();
        const personStore = usePersonStore();

        const user = ref(null as AuthUser | null);
        
        // Use session cookie for access token (no expiration)
        const tokenCookie = useCookie<string | null>('auth._token.pb', {
            default: () => null,
            secure: true,
            domain: config.public.pb.domainHost,
            sameSite: 'lax',
            // No expires - makes it a session cookie
        });

        const refreshTokenCookie = useCookie<string | null>('auth._refresh_token.pb', {
            default: () => null,
            secure: true,
            domain: config.public.pb.domainHost,
            expires: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)),
            sameSite: 'lax',
        });

        const token = computed(() => tokenCookie.value);
        const isRefreshing = ref(false);
        const isLoggingOut = ref(false);
        let refreshPromise: Promise<boolean> | null = null;
        
        // Request queue for handling requests during token refresh
        interface QueuedRequest {
            resolve: (token: string | null) => void;
            reject: (error: any) => void;
        }
        const requestQueue: QueuedRequest[] = [];

        const loggedIn = computed(() => !!token.value);

        // Function to get a valid token for requests - handles refresh and queueing
        async function getValidTokenForRequest(): Promise<string | null> {
            // If we have a token, return it (it might be expired, but 401 retry logic will handle that)
            if (token.value) {
                return token.value;
            }

            // If already refreshing, queue the request
            if (isRefreshing.value) {
                return new Promise((resolve, reject) => {
                    requestQueue.push({ resolve, reject });
                });
            }

            // No token, try to refresh
            const refreshed = await refreshAccessToken();
            return refreshed ? token.value : null;
        }

        // Auto-refresh token using refresh token cookie
        async function refreshAccessToken(): Promise<boolean> {
            if (!refreshTokenCookie.value) {
                // No refresh token, process queue with null 
                processRequestQueue(null);
                
                return false;
            }
            
            // If already refreshing, return the existing promise
            if (refreshPromise) {
                return refreshPromise;
            }
            
            isRefreshing.value = true;
            refreshPromise = (async (): Promise<boolean> => {
                try {
                    // Use standard OAuth2 refresh flow
                    const response = await axios.post<PbAccessToken>(`${config.public.pb.authHost}/oauth/token`, {
                        grant_type: 'refresh_token',
                        refresh_token: refreshTokenCookie.value,
                        client_id: config.public.pb.authClientId,
                    });

                    await setToken(response.data);
                    
                    // Process queued requests with new token
                    processRequestQueue(token.value);
                    return true;
                } catch (error: any) {
                    // Refresh failed, clear tokens and redirect to login
                    tokenCookie.value = null;
                    refreshTokenCookie.value = null;
                    user.value = null;
                    
                    // Process queue with null (fail all requests)
                    processRequestQueue(null);
                    
                    // Redirect to login (but not during OAuth callback flow)
                    if (import.meta.client && !window.location.pathname.startsWith('/auth/callback')) {
                        await navigateTo(`${config.public.pb.authHost}/login?redirect=${encodeURIComponent(window.location.href)}`, { external: true });
                    }
                    return false;
                } finally {
                    isRefreshing.value = false;
                    refreshPromise = null;
                }
            })();
            
            return refreshPromise;
        }

        // Process the request queue after refresh completes
        function processRequestQueue(newToken: string | null) {
            while (requestQueue.length > 0) {
                const queuedRequest = requestQueue.shift();
                if (queuedRequest) {
                    if (newToken) {
                        queuedRequest.resolve(newToken);
                    } else {
                        queuedRequest.reject(new Error('Token refresh failed'));
                    }
                }
            }
        }

        // Initialize authentication on app start
        async function initializeAuth(): Promise<boolean> {
            // If we already have a session token, we're good
            if (token.value) {
                return true;
            }

            // No token, try to refresh
            return await refreshAccessToken();
        }

        async function getMe() {
            try {
                // If we have a token, use it directly
                if (token.value) {
                    const userData = await authService.getMe();
                    user.value = userData;
                    return;
                }

                // No token, try to get one (will handle refresh if needed)
                const validToken = await getValidTokenForRequest();
                if (!validToken) {
                    throw new Error('No valid token available');
                }

                const userData = await authService.getMe();
                user.value = userData;
            } catch (error: any) {
                tokenCookie.value = null;
                user.value = null;
            }
        }

        // Set token (from OAuth flow)
        async function setToken(tokenData: PbAccessToken) {
            // Set access token as session cookie (no expiration)
            tokenCookie.value = 'Bearer ' + tokenData.access_token;
            
            // Set refresh token with 30-day expiration
            const refreshTokenExpires = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)); // 30 days
            const refreshCookie = useCookie<string | null>('auth._refresh_token.pb', {
                default: () => null,
                secure: true,
                domain: config.public.pb.domainHost,
                sameSite: 'lax',
                expires: refreshTokenExpires,
            });
            refreshCookie.value = tokenData.refresh_token;
            refreshTokenCookie.value = tokenData.refresh_token;
        }

        async function logout() {
            // Set logout state to prevent auto-refresh
            isLoggingOut.value = true;
            // Clear cookies and state
            tokenCookie.value = null;
            refreshTokenCookie.value = null;
            user.value = null;
            localStorage.clear();

            const clearAuthCookies = () => {
                const cookiesToClear = [
                    "auth._token.pb",
                    "auth._refresh_token.pb", 
                    "XSRF-TOKEN",
                ];

                const domains = [
                    window.location.hostname,
                ];

                const paths = ["/"];

                cookiesToClear.forEach((cookieName) => {
                    domains.forEach((domain) => {
                        paths.forEach((path) => {
                            document.cookie = `${cookieName}=; path=${path}; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax; Secure`;
                        });
                    });
                });
            };

            clearAuthCookies();
            window.location.href = `${config.public.pb.authHost}/logout?redirect=${config.public.pb.appDomain}`;
            isLoggingOut.value = false;
        }

        // Initiate login by redirecting to auth app
        async function login(redirectPath: string = '/dashboard') {
            const state = useCookie('pb-auth-state');
            state.value = encryption.generateRandomString();
            const challenge = await encryption.getChallenge(state.value);

            const redirectCookie = useCookie('pb-redirect');

            redirectCookie.value = redirectPath;
            window.location.href = `${config.public.pb.authHost}/oauth/authorize?protocol=oauth2&client_id=${config.public.pb.authClientId}&redirect_uri=${config.public.pb.authRedirectUri}&response_type=${config.public.pb.authResponseType}&scope=&state=${state.value}&code_challenge=${challenge}&code_challenge_method=S256&flow=pb`;
        }

        async function exchangeToken(_token: string) {
            const state = useCookie('pb-auth-state');
            const { data } = await axios.post<PbAccessToken>(`${config.public.pb.authHost}/oauth/token`, {
                protocol: 'oauth2',
                grant_type: 'authorization_code',
                client_id: config.public.pb.authClientId,
                redirect_uri: config.public.pb.authRedirectUri,
                code_verifier: state.value,
                code: _token,
            });

            await setToken(data);

            await getMe();
            await personStore.getPerson();
        }

        // Auto-refresh token when it becomes null (unless we're logging out)
        // Note: With session tokens, this mainly handles explicit token clearing/logout
        watch(tokenCookie, async (newToken, oldToken) => {
            // Only trigger if token actually changed from something to null
            // and we're not logging out and not already refreshing
            if (oldToken && !newToken && !isLoggingOut.value && !isRefreshing.value && refreshTokenCookie.value) {
                await refreshAccessToken();
                
            }
        }, { flush: 'sync' }); // Use sync to avoid batching issues

        return {
            user,
            token: readonly(token), // Make token read-only
            loggedIn,
            getMe,
            login,
            logout,
            setToken,
            refreshAccessToken,
            getValidTokenForRequest,
            initializeAuth,
            exchangeToken,
            isRefreshing: readonly(isRefreshing),
        };
    },
    {
        persist: {
            // Only persist user data, NOT tokens
            pick: ['user'],
        }
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
